import React, { useState, useRef, useEffect } from "react";
import { NavLink, useLocation, useMatch } from 'react-router-dom'; 
import { slide as Menu } from 'react-burger-menu';
import pccmlogo from ".././assets/pccm_logo.png"; 
import ".././styles/Navbar.css"; 

function Navbar() {
    const [isOrgDropdownOpen, setIsOrgDropdownOpen] = useState(false);
    const [isCommunityDropdownOpen, setIsCommunityDropdownOpen] = useState(false);
    const [isRentalDropdownOpen, setIsRentalDropdownOpen] = useState(false);
    const [isBurgerMenuOpen, setIsBurgerMenuOpen] = useState(false);

    const location = useLocation(); // Get current location object

    // Function to check if any of the dropdown's child routes are active
    const matchAboutUs = useMatch("/aboutus/*");
    const matchConstitution = useMatch("/constitution/*");
    const matchReport = useMatch("/report/*");
    const isAnyOrgChildActive = matchAboutUs || matchConstitution || matchReport;

    const matchNews = useMatch("/news/*");
    const matchEvents = useMatch("/events/*");
    const matchGallery = useMatch("/gallery/*");
    const isAnyCommunityChildActive = matchNews || matchEvents || matchGallery;

    const matchSpaces = useMatch("/spaces/*");
    const matchAdvertise = useMatch("/advertise/*");
    const isAnyRentalChildActive = matchSpaces || matchAdvertise;

    //Handler for burger menu state changes
    const handleBurgerStateChange = (state) => {
        setIsBurgerMenuOpen(state.isOpen);
    };

    //Function to close burger menu
    const closeBurgerMenu = () => {
        setIsBurgerMenuOpen(false);
    };

    // Create refs for each dropdown's parent <li> element to detect clicks outside
    const orgDropdownRef = useRef(null);
    const communityDropdownRef = useRef(null);
    const rentalDropdownRef = useRef(null);

    // This function handles clicking the "Our Organization" button
    const handleOrgClick = (event) => {
        event.stopPropagation(); // Prevent click from propagating to document
        setIsOrgDropdownOpen(prev => !prev); // Toggle its own state
        setIsCommunityDropdownOpen(false); // Close other dropdowns
        setIsRentalDropdownOpen(false); // Close other dropdowns
    };

    // This function handles clicking the "Our Community" button
    const handleCommunityClick = (event) => {
        event.stopPropagation();
        setIsCommunityDropdownOpen(prev => !prev);
        setIsOrgDropdownOpen(false);
        setIsRentalDropdownOpen(false);
    };

    // This function handles clicking the "Rentals" button
    const handleRentalClick = (event) => {
        event.stopPropagation();
        setIsRentalDropdownOpen(prev => !prev);
        setIsOrgDropdownOpen(false);
        setIsCommunityDropdownOpen(false);
    };

    // Handler to close all dropdowns, typically called when a link inside a dropdown is clicked
    const closeAllDropdowns = () => {
        setIsOrgDropdownOpen(false);
        setIsCommunityDropdownOpen(false);
        setIsRentalDropdownOpen(false);
    };

    // Effect to handle clicks outside the dropdowns to close them
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check if a dropdown is open and the click is outside its container
            if (isOrgDropdownOpen && orgDropdownRef.current && !orgDropdownRef.current.contains(event.target)) {
                closeAllDropdowns();
            }
            if (isCommunityDropdownOpen && communityDropdownRef.current && !communityDropdownRef.current.contains(event.target)) {
                closeAllDropdowns();
            }
            if (isRentalDropdownOpen && rentalDropdownRef.current && !rentalDropdownRef.current.contains(event.target)) {
                closeAllDropdowns();
            }
        };

        // Add event listener when component mounts
        document.addEventListener('mousedown', handleClickOutside);

        // Clean up event listener when component unmounts
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOrgDropdownOpen, isCommunityDropdownOpen, isRentalDropdownOpen]); // Re-run effect if dropdown states change

    // Close dropdowns when route changes (e.g., clicking a link within the dropdown)
    useEffect(() => {
        closeAllDropdowns();
    }, [location.pathname]); // Close dropdowns whenever the path changes

    return (
        <div className="navbar">
            <header className="wrapper header">
                <NavLink to="/" className="sml-logo-link">
                    <img src={pccmlogo} className="sml-logo" alt="pccm-logo"></img>
                </NavLink>
                <nav>
                    <ul>
                        {/* Our Organization Dropdown */}
                        <li 
                            className={`dropdown ${isOrgDropdownOpen ? 'open' : ''} ${isAnyOrgChildActive ? 'active-dropdown-parent' : ''}`} 
                            onClick={handleOrgClick} 
                            ref={orgDropdownRef}
                        >
                            <span className="dropdown-button">Our Organization {isOrgDropdownOpen ? '▲' : '▼'}</span>
                            <ul className="dropdown-menu">
                                <li>
                                    <NavLink
                                        to="/aboutus"
                                        className={({ isActive }) => isActive ? 'active-dropdown-item' : ''}
                                        onClick={closeAllDropdowns}
                                    >
                                        About Us
                                    </NavLink>
                                </li>
                                <li>
                                    <NavLink
                                        to="/constitution"
                                        className={({ isActive }) => isActive ? 'active-dropdown-item' : ''}
                                        onClick={closeAllDropdowns}
                                    >
                                        Constitution & By-Laws
                                    </NavLink>
                                </li>
                                <li>
                                    <NavLink
                                        to="/report"
                                        className={({ isActive }) => isActive ? 'active-dropdown-item' : ''}
                                        onClick={closeAllDropdowns}
                                    >
                                        Finance & Report
                                    </NavLink>
                                </li>
                            </ul>
                        </li>

                        {/* Our Community Dropdown */}
                        <li 
                            className={`dropdown ${isCommunityDropdownOpen ? 'open' : ''} ${isAnyCommunityChildActive ? 'active-dropdown-parent' : ''}`} 
                            onClick={handleCommunityClick} 
                            ref={communityDropdownRef}
                        > 
                            <span className="dropdown-button">Our Community {isCommunityDropdownOpen ? '▲' : '▼'}</span>
                            <ul className="dropdown-menu">
                                <li>
                                    <NavLink
                                        to="/news"
                                        className={({ isActive }) => isActive ? 'active-dropdown-item' : ''}
                                        onClick={closeAllDropdowns}
                                    >
                                        News & Announcement
                                    </NavLink>
                                </li>
                                <li>
                                    <NavLink
                                        to="/events"
                                        className={({ isActive }) => isActive ? 'active-dropdown-item' : ''}
                                        onClick={closeAllDropdowns}
                                    >
                                        Events & Services
                                    </NavLink>
                                </li>
                                <li>
                                    <NavLink
                                        to="/gallery"
                                        className={({ isActive }) => isActive ? 'active-dropdown-item' : ''}
                                        onClick={closeAllDropdowns}
                                    >
                                        Gallery
                                    </NavLink>
                                </li>
                            </ul>
                        </li>

                        {/* Rentals Dropdown */}
                        <li 
                            className={`dropdown ${isRentalDropdownOpen ? 'open' : ''} ${isAnyRentalChildActive ? 'active-dropdown-parent' : ''}`} 
                            onClick={handleRentalClick} 
                            ref={rentalDropdownRef}
                        > 
                            <span className="dropdown-button">Rentals {isRentalDropdownOpen ? '▲' : '▼'}</span>
                            <ul className="dropdown-menu">
                                <li>
                                    <NavLink
                                        to="/spaces"
                                        className={({ isActive }) => isActive ? 'active-dropdown-item' : ''}
                                        onClick={closeAllDropdowns}
                                    >
                                        Community Spaces
                                    </NavLink>
                                </li>
                                <li>
                                    <NavLink
                                        to="/advertise"
                                        className={({ isActive }) => isActive ? 'active-dropdown-item' : ''}
                                        onClick={closeAllDropdowns}
                                    >
                                        Advertise with Us
                                    </NavLink>
                                </li>
                            </ul>
                        </li>

                        {/* Main Nav Links (non-dropdown) */}
                        <li>
                            <NavLink
                                to="/memberships"
                                className={({ isActive }) => isActive ? 'active-nav-item' : ''}
                            >
                                Memberships
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/contact"
                                className={({ isActive }) => isActive ? 'active-nav-item' : ''}
                            >
                                Contact Us
                            </NavLink>
                        </li>

                    </ul>
                </nav>
                <div id="nav-buttons">
                    <NavLink to="/donate" className="button-1" id="donate-button"><h4>Donate</h4></NavLink>
                    
                    <div id="outer-container"> 
                        <Menu width={"50%"} right outerContainerId={"outer-container"} pageWrapId={"root"} isOpen={isBurgerMenuOpen} onStateChange={handleBurgerStateChange}
                        >
                            {/* Burger menu items also use NavLink */}
                            {/* <NavLink to="/" className="bm-item menu-item topMenu" onClick={closeBurgerMenu}><h3>Home</h3></NavLink>
                            <div className="line"></div> */}
                            <NavLink to="/aboutus" className="bm-item menu-item" onClick={closeBurgerMenu}><h3>About Us</h3></NavLink>
                            <NavLink to="/constitution" className="bm-item menu-item" onClick={closeBurgerMenu}><h3>Constitution & By-Laws</h3></NavLink>
                            <NavLink to="/report" className="bm-item menu-item" onClick={closeBurgerMenu}><h3>Finance & Report</h3></NavLink>
                            <div className="line-divider"></div>
                            <NavLink to="/news" className="bm-item menu-item" onClick={closeBurgerMenu}><h3>News & Announcement</h3></NavLink>
                            <NavLink to="/events" className="bm-item menu-item" onClick={closeBurgerMenu}><h3>Events & Services</h3></NavLink>
                            <NavLink to="/gallery" className="bm-item menu-item" onClick={closeBurgerMenu}><h3>Gallery</h3></NavLink>
                            <div className="line-divider"></div>
                            <NavLink to="/spaces" className="bm-item menu-item" onClick={closeBurgerMenu}><h3>Community Spaces</h3></NavLink>
                            <NavLink to="/advertise" className="bm-item menu-item" onClick={closeBurgerMenu}><h3>Advertise with Us</h3></NavLink>
                            <div className="line-divider"></div>
                            <NavLink to="/memberships" className="bm-item menu-item" onClick={closeBurgerMenu}><h3>Memberships</h3></NavLink>
                            <NavLink to="/contact" className="bm-item menu-item" onClick={closeBurgerMenu}><h3>Contact Us</h3></NavLink>
                            <NavLink to="/donate" className="bm-item menu-item donate-bm" onClick={closeBurgerMenu}><h3>Donate</h3></NavLink>
                        </Menu>
                    </div>
                </div>
            </header>
        </div>
    );
}

export default Navbar;